import { pushToDataLayer } from "@/lib/analytics";

/** All listeners and timers belong to a single mounted page. */
export function initializePage(root: HTMLElement, toggleTheme: () => void) {
  const controller = new AbortController();
  const signal = controller.signal;
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, delay: number) => {
    const timer = setTimeout(() => { timers.delete(timer); fn(); }, delay);
    timers.add(timer);
    return timer;
  };
  const header = root.querySelector(".site-header");
  const scroll = () => header?.classList.toggle("scrolled", window.scrollY > 8);
  window.addEventListener("scroll", scroll, { passive: true, signal });
  scroll();

  const navItems = Array.from(root.querySelectorAll<HTMLElement>(".nav-item"));
  const clickedMenus = new WeakSet<HTMLElement>();
  let menuLeave: ReturnType<typeof setTimeout> | undefined;
  const cancelMenuLeave = () => {
    if (menuLeave) { clearTimeout(menuLeave); timers.delete(menuLeave); menuLeave = undefined; }
  };
  const closeMenus = (except?: HTMLElement) => navItems.forEach(item => {
    if (item === except) return;
    item.classList.remove("open");
    clickedMenus.delete(item);
    item.querySelector(".nav-trigger")?.setAttribute("aria-expanded", "false");
  });
  closeMenus();
  const openMenu = (item: HTMLElement) => {
    cancelMenuLeave();
    closeMenus(item);
    item.classList.add("open");
    item.querySelector(".nav-trigger")?.setAttribute("aria-expanded", "true");
  };
  navItems.forEach(item => {
    item.addEventListener("pointerenter", event => {
      if (event.pointerType === "touch") return;
      openMenu(item);
    }, { signal });
    item.addEventListener("pointerleave", event => {
      if (event.pointerType === "touch") return;
      cancelMenuLeave();
      menuLeave = later(() => {
        if (!item.contains(document.activeElement)) closeMenus();
      }, 300);
    }, { signal });
    item.addEventListener("focusout", event => {
      if (!item.contains(event.relatedTarget as Node | null) && !item.matches(":hover")) closeMenus();
    }, { signal });
  });

  const car = root.querySelector<HTMLElement>(".car");
  const tabs = Array.from(car?.querySelectorAll<HTMLElement>(".car-tab") ?? []);
  const panels = Array.from(car?.querySelectorAll<HTMLElement>(".car-panel") ?? []);
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let index = 0;
  let paused = false;
  let hovering = false;
  let carouselTimer: ReturnType<typeof setTimeout> | undefined;
  const restart = () => {
    if (carouselTimer) clearTimeout(carouselTimer);
    if (!reduce && !paused && !hovering && tabs.length > 1) carouselTimer = later(() => show(index + 1), 7000);
  };
  const show = (next: number, user = false) => {
    if (!tabs.length) return;
    index = (next + tabs.length) % tabs.length;
    tabs.forEach((tab, n) => {
      tab.setAttribute("aria-selected", String(n === index));
      tab.tabIndex = n === index ? 0 : -1;
      const bar = tab.querySelector<HTMLElement>(".bar i");
      if (bar) { bar.style.animation = "none"; void bar.offsetWidth; bar.style.animation = ""; }
    });
    panels.forEach((panel, n) => { panel.classList.toggle("active", n === index); panel.hidden = n !== index; });
    if (user) pushToDataLayer({ event: "carousel_select", carousel_slide: tabs[index].dataset.slide });
    restart();
  };
  car?.addEventListener("mouseenter", () => { hovering = true; restart(); }, { signal });
  car?.addEventListener("mouseleave", () => { hovering = false; restart(); }, { signal });
  tabs.forEach((tab, n) => {
    tab.addEventListener("click", () => show(n, true), { signal });
    tab.addEventListener("keydown", event => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault(); show(index + (event.key === "ArrowRight" ? 1 : -1), true); tabs[index].focus();
      }
    }, { signal });
  });
  show(0);

  const click = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const trigger = target.closest<HTMLElement>(".nav-trigger");
    if (trigger) {
      const item = trigger.closest<HTMLElement>(".nav-item");
      if (item) {
        if (item.classList.contains("open") && clickedMenus.has(item)) {
          closeMenus(); clickedMenus.delete(item);
        } else { openMenu(item); clickedMenus.add(item); }
      }
    } else if (target.closest("a[href]") && target.closest(".site-header, .mobile-panel")) {
      cancelMenuLeave();
      closeMenus();
      root.querySelector(".mobile-panel")?.classList.remove("open");
      root.querySelector(".burger")?.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      if (document.activeElement instanceof HTMLElement && document.activeElement.closest(".nav-item, .mobile-panel")) {
        document.activeElement.blur();
      }
    } else if (!target.closest(".nav-item")) closeMenus();
    if (target.closest(".theme-toggle")) toggleTheme();
    const burger = target.closest(".burger");
    if (burger) {
      const open = root.querySelector(".mobile-panel")?.classList.toggle("open") ?? false;
      burger.setAttribute("aria-expanded", String(open));
      document.body.style.overflow = open ? "hidden" : "";
    }
    const control = target.closest<HTMLElement>("[data-car]");
    if (control?.dataset.car === "prev") show(index - 1, true);
    if (control?.dataset.car === "next") show(index + 1, true);
    if (control?.dataset.car === "pause") {
      paused = !paused; car?.classList.toggle("paused", paused);
      control.setAttribute("aria-label", paused ? "Play carousel" : "Pause carousel");
      control.innerHTML = paused ? '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5z"/></svg>' : '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M3 2.5h3v11H3zm7 0h3v11h-3z"/></svg>';
      restart();
    }
    const scroller = target.closest<HTMLElement>("[data-hs]");
    if (scroller?.dataset.hs) {
      const element = document.getElementById(scroller.dataset.hs);
      element?.scrollBy({ left: (element.clientWidth * .8) * Number(scroller.dataset.dir), behavior: reduce ? "instant" : "smooth" });
    }
    const track = target.closest<HTMLAnchorElement>("[data-track]");
    if (track) pushToDataLayer({ event: track.dataset.track, cta_id: track.dataset.cta ?? "", cta_location: track.dataset.loc ?? "", link_url: track.href ?? "" });
  };
  root.addEventListener("click", click, { signal });
  document.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      const active = navItems.find(item => item.classList.contains("open"));
      closeMenus(); active?.querySelector<HTMLElement>(".nav-trigger")?.focus();
      root.querySelector(".mobile-panel")?.classList.remove("open");
      root.querySelector(".burger")?.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
    }
  }, { signal });
  root.querySelectorAll<HTMLElement>(".sol-p").forEach(link => {
    const activate = () => {
      root.querySelectorAll<HTMLElement>(".sol-p,.sol-pane").forEach(el => el.classList.toggle("on", el.dataset.p === link.dataset.p));
    };
    link.addEventListener("mouseenter", activate, { signal });
    link.addEventListener("focus", activate, { signal });
  });
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) { entry.target.classList.add("in"); observer.unobserve(entry.target); }
  }), { rootMargin: "0px 0px -4% 0px" });
  root.querySelectorAll(".rv").forEach(el => reduce ? el.classList.add("in") : observer.observe(el));
  return () => { controller.abort(); timers.forEach(clearTimeout); observer.disconnect(); document.body.style.overflow = ""; };
}